/**
 * Renaming a project, and editing its description.
 *
 * A **real persisted edit**: `PATCH /projects/{id}` writes the row and the
 * caller re-reads. Nothing here changes local state and hopes - if the request
 * fails, the old name is still on screen, which is the truth.
 *
 * Three fields. The subject type is not offered and
 * that is a product decision rather than an omission: the whole perception
 * policy - which analyses run, which coverage model applies, what the phone's
 * live guidance says - is derived from `subject_type`, and changing it would
 * leave stored analyses described by rules that did not produce them. The
 * backend refuses the field outright, so a control here would be a form that
 * 422s.
 *
 * Clearing the description is a supported edit. The client sends
 * `description: null` for an emptied field rather than omitting it, and the
 * backend distinguishes "absent" from "explicitly null" by which keys the body
 * carried - without that, a description could be set but never removed.
 *
 * The geometry mode *is* offered, and the asymmetry with subject type is the
 * point rather than an inconsistency. Subject type describes what past
 * analyses were; the mode governs what the next reconstruction may do, and
 * nothing already stored is re-described by changing it. Turning it back down
 * does not convert completed geometry into observed geometry - each finished
 * job keeps the mode it ran under - so this control is safe in both
 * directions, which is exactly what the note under it says.
 */

import { useRef, useState } from "react";
import { updateProject } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { GeometryMode, Project } from "@/api/types";
import { Field } from "@/components/Field";
import { Button } from "@/components/ui";
import { GEOMETRY_MODE_CHOICES, GEOMETRY_MODE_TERMS } from "@/i18n/terms";
import { useDialogFocus } from "@/lib/useDialogFocus";

/** Mirrors the backend's own limits so the form says the rule before it breaks. */
const NAME_MAX = 200;
const DESCRIPTION_MAX = 2000;

interface Props {
  project: Project;
  onCancel: () => void;
  /** Called with the saved project, so the caller can refresh from truth. */
  onSaved: (project: Project) => void;
}

export function EditProjectDialog({ project, onCancel, onSaved }: Props) {
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [geometryMode, setGeometryMode] = useState<GeometryMode>(
    project.geometry_mode ?? "precise",
  );
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const dialog = useRef<HTMLDivElement>(null);

  // Focus on open, Escape while it is willing to close, and focus back on the
  // button that opened it. `busy` is read at the moment Escape arrives rather
  // than being an effect dependency: as a dependency it re-ran the effect on
  // every save and pulled focus out of the field somebody was typing in.
  useDialogFocus(dialog, { onEscape: onCancel, escapeEnabled: !busy });

  const trimmedName = name.trim();
  const nameValid = trimmedName.length > 0 && trimmedName.length <= NAME_MAX;
  const trimmedDescription = description.trim();
  const changed =
    trimmedName !== project.name ||
    trimmedDescription !== (project.description ?? "") ||
    geometryMode !== (project.geometry_mode ?? "precise");

  async function save() {
    if (!nameValid || busy) return;
    setBusy(true);
    setFailure(null);
    try {
      const saved = await updateProject(project.id, {
        name: trimmedName,
        // An emptied field is an explicit null, which is what removes the
        // description. Sending "" would store an empty string instead.
        description: trimmedDescription === "" ? null : trimmedDescription,
        geometry_mode: geometryMode,
      });
      onSaved(saved);
    } catch (error) {
      setFailure(
        error instanceof ApiError && error.status === 422
          ? "Proje adı geçersiz. Ad boş olamaz."
          : error instanceof ApiError && error.status === 404
            ? "Proje bulunamadı."
            : "Değişiklikler kaydedilemedi. Lütfen tekrar deneyin.",
      );
      setBusy(false);
    }
  }

  return (
    <div className="pairing-backdrop" role="presentation" onClick={busy ? undefined : onCancel}>
      <div
        className="pairing-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-project-title"
        tabIndex={-1}
        ref={dialog}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="edit-project-title">Projeyi düzenle</h2>

        <Field label="Proje adı" required>
          {(props) => (
            <input
              {...props}
              value={name}
              maxLength={NAME_MAX}
              autoComplete="off"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field label="Açıklama">
          {(props) => (
            <textarea
              {...props}
              value={description}
              maxLength={DESCRIPTION_MAX}
              rows={4}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>

        <Field
          label="Bu modeli ne için kullanacaksınız?"
          hint="Bu seçim yalnızca bundan sonraki işlemleri etkiler; tamamlanmış modeller olduğu gibi kalır."
        >
          {(props) => (
            <select
              {...props}
              value={geometryMode}
              onChange={(event) => setGeometryMode(event.target.value as GeometryMode)}
            >
              {GEOMETRY_MODE_CHOICES.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          )}
        </Field>
        <p className="pairing-muted">{GEOMETRY_MODE_TERMS[geometryMode].note}</p>

        <p className="pairing-muted">
          Proje türü değiştirilemez. Analiz, kapsama ve rehberlik davranışı proje
          türünden türetilir; sonradan değiştirmek mevcut sonuçları üretmemiş
          kurallarla açıklamak olurdu.
        </p>

        {failure && (
          <p className="pairing-badge pairing-badge--warn" role="alert">
            <span aria-hidden="true">!</span> {failure}
          </p>
        )}

        <div className="pairing-actions">
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Vazgeç
          </Button>
          <Button
            variant="primary"
            onClick={() => void save()}
            disabled={!nameValid || !changed || busy}
            busy={busy}
          >
            Kaydet
          </Button>
        </div>
      </div>
    </div>
  );
}
